import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";

import { Box, Button, Container, Flex, Heading, Text } from "@chakra-ui/react";

import { CollectionGrid } from "components/collections/collection-grid";
import SidebarCollapse from "components/global/sidebar-collapse";
import {
  DataDisplayUtilityBox,
  GridSizeUtility,
  SearchInputUtility,
} from "components/items/item-utility";
import MainIndex from "components/layout/main-index";
import MainContainer from "components/main-container";

import { formatDistance } from "date-fns";
import { IoChevronDown } from "react-icons/io5";

import { GraphQLQuery } from "@aws-amplify/api";
import { API } from "aws-amplify";
import {
  Collection as CollectionState,
  CollectionStatus,
  SearchCollectionsQuery,
} from "src/API";
import { searchCollections } from "src/graphql/queries";

import { useDebounce } from "utils/helpers";
import { GridSizeState } from "utils/interface";

interface CollectionsProps {
  loaded: boolean;
  collections: CollectionState[];
}

const Collection = () => {
  const router = useRouter();
  const { search } = router.query;

  const [tabList, setTabList] = useState<Array<string>>([]);
  const [collections, setCollections] = useState<CollectionsProps>({
    loaded: false,
    collections: [],
  });
  const [grid, setGrid] = useState<GridSizeState>({ size: "medium" });
  const [collectionNextToken, setCollectionNextToken] = useState<string>("");
  const [collectionsData, setCollectionsData] = useState<{
    dateNow: Date;
    lastUpdated: Date;
    total: number;
  }>({
    dateNow: new Date(),
    lastUpdated: new Date(),
    total: 0,
  });
  const [trigger, setTrigger] = useState<boolean>();
  const [searchInput, setSearchInput] = useState<string>(
    search ? String(search) : ""
  );
  const debounceInput = useDebounce(searchInput, 500);

  useEffect(() => {
    setTabList([
      "Trending",
      "Top",
      "Art",
      "Collectibles",
      "Domain Name",
      "Music",
      "Photography",
      "Sports",
      "Trading Cards",
      "Utility",
      "Virtual Wordls",
    ]);
  }, []);

  useEffect(() => {
    const getListCollections = async () => {
      setCollections((c) => ({ ...c, loaded: false }));
      try {
        const collection = await API.graphql<
          GraphQLQuery<SearchCollectionsQuery>
        >({
          query: searchCollections,
          variables: {
            limit: 18,
            filter:
              debounceInput === ""
                ? {
                    and: [
                      { status: { ne: CollectionStatus.COMING_SOON } },
                      { status: { ne: CollectionStatus.DRAFT } },
                    ],
                  }
                : {
                    and: [
                      { status: { ne: CollectionStatus.COMING_SOON } },
                      { status: { ne: CollectionStatus.DRAFT } },
                    ],
                    name: {
                      matchPhrasePrefix: `*${debounceInput}`,
                    },
                  },
            nextToken: collectionNextToken == "" ? null : collectionNextToken,
          },
        });

        const newCollectionList = collection.data?.searchCollections
          ?.items as CollectionState[];

        if (newCollectionList) {
          setCollections((c) => ({
            loaded: true,
            collections: [...c.collections, ...newCollectionList],
          }));

          setCollectionsData((d) => ({
            dateNow: new Date(),
            lastUpdated: new Date(),
            total: collection.data?.searchCollections?.total ?? 0,
          }));

          setCollectionNextToken(
            collection.data?.searchCollections?.nextToken ?? ""
          );
        }
      } catch (error) {
        console.log(error);
      }
    };

    if (trigger) {
      setTrigger((t) => !t);
      if (collectionNextToken !== null) {
        getListCollections();
      }
    }
  }, [trigger, collections, collectionNextToken, debounceInput]);

  useEffect(() => {
    if (debounceInput || debounceInput == "") {
      setCollections({ loaded: false, collections: [] });
      setCollectionNextToken("");
      setTrigger(true);
    }
  }, [debounceInput]);

  useEffect(() => {
    if (search) {
      setSearchInput(String(search));
    }
  }, [search]);

  useEffect(() => {
    // Sync date every 1 minute
    const timer = setInterval(() => {
      setCollectionsData((d) => ({ ...d, dateNow: new Date() }));
    }, 60 * 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <MainContainer title="Explore Collections" maxW={"100%"} p={0}>
      <Box display="Flex" justifyContent="center" alignContent="center" mt={8}>
        <Heading as="h1" textAlign={"center"}>
          Explore Collections
        </Heading>
      </Box>
      {/* <CollectionTab tabList={tabList} /> */}
      <Container maxW={"full"} px={0}>
        <SidebarCollapse
          topBar={
            <>
              <DataDisplayUtilityBox>
                <SearchInputUtility
                  width={{ base: "100%" }}
                  flex={1}
                  flexBasis={{ base: "100%", md: "auto" }}
                  value={searchInput}
                  handler={(value: string) => setSearchInput(value)}
                />
                <GridSizeUtility />
              </DataDisplayUtilityBox>
              <Flex
                mt={4}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Text>
                  Updated{" "}
                  {formatDistance(
                    collectionsData.lastUpdated,
                    collectionsData.dateNow,
                    {
                      addSuffix: true,
                    }
                  )}
                </Text>
                <Text>
                  {Intl.NumberFormat("en-IN", {
                    maximumSignificantDigits: 3,
                  }).format(collectionsData.total)}{" "}
                  {collectionsData.total > 1 ? "collections" : "collection"}
                </Text>
              </Flex>
            </>
          }
          sideBar={<></>}
          mainBar={
            <>
              <CollectionGrid
                collections={collections.collections}
                loaded={collections.loaded}
              />
              <Box textAlign={"center"} mt={12}>
                {collectionNextToken && (
                  <Button
                    variant={"gradient"}
                    rightIcon={<IoChevronDown />}
                    onClick={() => {
                      setTrigger(true);
                    }}
                  >
                    Load More
                  </Button>
                )}
              </Box>
            </>
          }
        />
      </Container>
    </MainContainer>
  );
};

Collection.getLayout = (page: ReactElement) => {
  return (
    <>
      <MainIndex>{page}</MainIndex>
    </>
  );
};

export default Collection;
